import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import { Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { formatPlanPauseDate } from 'src/functions/planPause.functions';
import type { tRetentionPlanPauseOffer } from '../../../../SubscriptionManager.types';
import { formatRetentionOfferAmount } from '../RetentionOfferModal/RetentionOfferModal.functions';

export type tPauseConfirmationModalProps = {
  isOpen: boolean;
  offer: tRetentionPlanPauseOffer;
  selectedMonths: 1 | 2 | 3;
  onClose: () => void;
};

export default function PauseConfirmationModal(
  props: tPauseConfirmationModalProps,
) {
  const { isOpen, offer, selectedMonths, onClose } = props;
  const { t, i18n } = useTranslation('settings/SubscriptionManager');
  const option =
    offer.pause.monthOptions.find((item) => item.months === selectedMonths) ??
    offer.pause.monthOptions[0];
  const formatDate = (instant: string) =>
    formatPlanPauseDate({ instant, locale: i18n.language });
  const restoreAmount = formatRetentionOfferAmount({
    amount: offer.pause.restoreAmount,
    currency: offer.pause.currency,
    locale: i18n.language,
  });

  return (
    <Modal open={isOpen} className="max-w-lg rounded-lg p-0">
      <Modal.Body className="flex flex-col items-center gap-6 p-6 text-center">
        <CheckCircleIcon className="size-16 text-success" aria-hidden="true" />
        <div className="space-y-2">
          <h2 className="text-xl font-bold">
            {t('cancelSubscription.pauseConfirmation.heading')}
          </h2>
          <p className="text-neutral-700">
            {t('cancelSubscription.pauseConfirmation.description', {
              startsAt: formatDate(option.startsAt),
              endsAt: formatDate(option.endsAt),
            })}
          </p>
        </div>
        <div className="w-full rounded-lg border border-neutral-200 p-4 text-left">
          <p className="font-bold">
            {t('cancelSubscription.pauseConfirmation.restoreHeading')}
          </p>
          <p className="mt-2" data-testid="pause-confirmation-restore">
            {t('cancelSubscription.pauseConfirmation.restoreDescription', {
              date: formatDate(option.endsAt),
              plan: offer.pause.restorePlanName,
              amount: restoreAmount ?? offer.pause.currency,
            })}
          </p>
        </div>
        <Button type="button" color="primary" block onClick={onClose}>
          {t('cancelSubscription.pauseConfirmation.closeButton')}
        </Button>
      </Modal.Body>
    </Modal>
  );
}
